import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, RefreshCw, Search, Sparkles, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ts } from '../api';
import { NumberSearch, CartPanel } from '../NumberPicker';
import { Page, Section, Field, Pill, VoicePill, SmsPill, money, useTS, ErrorNote } from '../ui';

const STEPS = ['Company', 'Main number', 'Plan & seats', 'Seat numbers', 'Payment', 'Activation'];

export default function TSSetup() {
  const session = useTS();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const company = session.company;
  const pricing = session.pricing;
  const [cart, setCart] = useState(null);
  const [step, setStep] = useState(0);

  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    ts.cart().then(setCart).catch(() => {}).finally(() => setCartLoaded(true));
  }, []);

  useEffect(() => {
    if (!cartLoaded) return;
    if (company.status === 'active') setStep(5);
    else if (company.status === 'pending_payment') setStep(4);
    else if (!company.legal_name || !company.address?.line1 || !company.website) setStep(0);
    else if (cart?.main_needed && !cart?.main) setStep(1);
    else if (!company.seats_requested) setStep(2);
    else setStep(3);
  }, [company.status, cartLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Page title="Set up your company phone system" subtitle="Your business gets one main number answered by an AI that knows your business, plus one number for every salesperson.">
      <ol className="flex flex-wrap gap-2 text-xs" data-testid="ts-setup-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`px-3 py-1 rounded-full ${i === step ? 'bg-violet-500/15 text-violet-600 dark:text-violet-400 font-medium' : i < step ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
            {i + 1}. {s}
          </li>
        ))}
      </ol>
      {step === 0 && <CompanyStep company={company} onDone={async () => { await session.refresh(); setStep(1); }} />}
      {(step === 1 || step === 2 || step === 3) && <WebsiteLearning />}
      {step === 1 && <MainNumberStep company={company} cart={cart} onCart={setCart}
        onBack={() => setStep(0)} onDone={() => setStep(2)} />}
      {step === 2 && <PlanPicker pricing={pricing} onPick={async (plan) => { setCart(await ts.setPlan(plan)); await session.refresh(); }} />}
      {step === 2 && <SeatsStep company={company} pricing={pricing} cart={cart}
        onDone={(c) => { setCart(c); session.refresh(); setStep(3); }} onBack={() => setStep(1)} />}
      {step === 3 && (
        <div className="space-y-4">
          <Section title="Choose a number for every seat" actions={<Button variant="ghost" size="sm" onClick={() => setStep(2)}>Change seats</Button>}>
            <NumberSearch cart={cart} onCart={setCart} />
          </Section>
          <CartPanel cart={cart} onCart={setCart} />
          <div className="flex justify-end">
            <Button disabled={!cart?.complete} onClick={() => setStep(4)} data-testid="ts-to-payment">Review & pay</Button>
          </div>
        </div>
      )}
      {step === 4 && <PaymentStep cart={cart} pricing={pricing} company={company} returning={params.get('checkout')}
        onBack={() => setStep(3)} onActive={async () => { await session.refresh(); setStep(5); }} />}
      {step === 5 && <ActivationStep company={company} onFinish={() => navigate('/telesales/staff')} />}
    </Page>
  );
}

function CompanyStep({ company, onDone }) {
  const [form, setForm] = useState({
    legal_name: company.legal_name || '', contact_phone: company.contact_phone || '', website: company.website || '',
    line1: company.address?.line1 || '', city: company.address?.city || '', state: company.address?.state || '',
    postal_code: company.address?.postal_code || '', ein: company.verification?.ein || '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!form.website.trim()) throw new Error('Your business website is required so our AI can learn your business');
      await ts.updateCompany({
        legal_name: form.legal_name, contact_phone: form.contact_phone, website: form.website,
        address: { line1: form.line1, city: form.city, state: form.state, postal_code: form.postal_code, country: 'US' },
        ...(form.ein ? { verification: { ein: form.ein } } : {}),
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Company details">
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ts-company-form">
        <ErrorNote error={error} />
        <Field label="Legal company name"><Input required value={form.legal_name} onChange={set('legal_name')} /></Field>
        <Field label="Company phone"><Input value={form.contact_phone} onChange={set('contact_phone')} placeholder="+1 555 123 4567" /></Field>
        <Field label="Street address"><Input required value={form.line1} onChange={set('line1')} /></Field>
        <Field label="City"><Input required value={form.city} onChange={set('city')} /></Field>
        <Field label="State"><Input required maxLength={2} value={form.state} onChange={set('state')} placeholder="CA" /></Field>
        <Field label="ZIP code"><Input required value={form.postal_code} onChange={set('postal_code')} /></Field>
        <Field label="Business website (our AI learns your business from it)"><Input required type="text" inputMode="url" value={form.website} onChange={set('website')} placeholder="https://yourbusiness.com" data-testid="ts-company-website" /></Field>
        <Field label="EIN (optional now, needed for business texting)"><Input value={form.ein} onChange={set('ein')} /></Field>
        <div className="sm:col-span-2 flex justify-end"><Button type="submit" disabled={busy} data-testid="ts-company-save">Continue</Button></div>
      </form>
    </Section>
  );
}

const PLAN_POINTS = {
  starter: ['Basic CRM and leads', 'Your own callbacks', 'Basic call reporting'],
  pro: ['CRM and campaigns', 'Callback Center', 'AI Front Desk', 'AI Brain basic tools', 'Call analytics'],
  business: ['Everything in Pro', 'Advanced callback workflows', 'Advanced AI Brain tools', 'Supervisor dashboard', 'Advanced analytics'],
};

export function PlanPicker({ pricing, current, onPick, label = 'Choose a plan' }) {
  const [busy, setBusy] = useState('');
  const selected = current || pricing.plan;
  const pick = async (id) => {
    if (id === selected) return;
    setBusy(id);
    try { await onPick(id); } catch (err) { toast.error(err.message); } finally { setBusy(''); }
  };
  return (
    <Section title={label}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ts-plan-picker">
        {pricing.plans.map(pl => (
          <button key={pl.id} type="button" onClick={() => pick(pl.id)} disabled={!!busy} data-testid={`ts-plan-${pl.id}`}
            className={`text-left rounded-lg border p-3 space-y-1 transition-colors ${pl.id === selected ? 'border-violet-500 bg-violet-500/10' : 'border-border hover:bg-muted'}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-foreground">{pl.name}</span>
              {pl.id === selected && <Pill tone="green">Selected</Pill>}
            </div>
            <div className="text-lg font-semibold text-foreground">{money(pl.seat_price_cents)}<span className="text-xs font-normal text-muted-foreground"> / seat / month</span></div>
            <div className="text-xs text-muted-foreground">{pl.included_minutes_per_seat} min · {pl.included_sms_per_seat} text segments per seat</div>
            <ul className="text-xs text-muted-foreground list-disc pl-4">{(PLAN_POINTS[pl.id] || []).map(x => <li key={x}>{x}</li>)}</ul>
          </button>
        ))}
      </div>
    </Section>
  );
}

function SeatsStep({ company, pricing, cart, onDone, onBack }) {
  const [seats, setSeats] = useState(company.seats_requested || 1);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { onDone(await ts.setSeats(Number(seats))); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <Section title="How many salespeople need a phone line?">
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Seats">
          <Input type="number" min={pricing.min_seats} max={pricing.max_seats} value={seats}
            onChange={e => setSeats(e.target.value)} className="w-28" data-testid="ts-seats-input" />
        </Field>
        <div className="text-sm text-foreground">
          {seats} × {money(pricing.seat_price_cents)} = <strong>{money(seats * pricing.seat_price_cents)}</strong> / month
        </div>
      </div>
      <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
        <li>{pricing.plan_name}: each seat includes one dedicated US phone number, {pricing.included_minutes_per_seat} conversation minutes and {pricing.included_sms_per_seat} text segments a month, pooled across the company.</li>
        <li>Only usage above the allowance is billed: {pricing.overage_cents_per_minute}¢ per minute, {pricing.overage_cents_per_sms}¢ per text segment{pricing.features.includes('ai_front_desk') ? `, and AI Front Desk at ${pricing.ai_cents_per_minute}¢ per AI minute` : ''}. You can set a spending limit.</li>
        <li>Add or reduce seats later from Billing.</li>
      </ul>
      {cart && cart.numbers_selected > 0 && <p className="text-xs text-muted-foreground">You've already picked {cart.numbers_selected} number(s).</p>}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button onClick={save} disabled={busy} data-testid="ts-seats-save">Continue</Button>
      </div>
    </Section>
  );
}

function PaymentStep({ cart, pricing, company, returning, onBack, onActive }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const tried = useRef(false);

  const polls = useRef(0);
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await ts.confirmCheckout();
      if (r.confirmed) { toast.success('Payment confirmed. Setting up your numbers…'); onActive(); return; }
      setNote(r.message || 'Payment is not complete yet.');
      // The bank is still answering: check again by itself for about two minutes.
      if (r.status === 'PROCESSING' && polls.current < 30) { polls.current += 1; setTimeout(confirm, 4000); }
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (returning && !tried.current) { tried.current = true; confirm(); }
  }, [returning]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setBusy(true);
    try {
      const r = await ts.checkout();
      if (r.simulated) { toast.message('Test mode: no card is charged.'); await confirm(); return; }
      if (r.hosted_payment_url) window.location.href = r.hosted_payment_url;
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const seats = cart?.seats_requested || company.seats_requested || 0;
  return (
    <Section title="Review and pay">
      <div className="text-sm space-y-1">
        <div>{pricing.plan_name} plan: {seats} seat{seats === 1 ? '' : 's'} × {money(pricing.seat_price_cents)} = <strong>{money(seats * pricing.seat_price_cents)} / month</strong></div>
        <div data-testid="ts-due-today">Charged today: <strong>{money(seats * pricing.seat_price_cents)}</strong> (your first month), in one charge on the payment page. Then the same amount renews monthly, plus any usage above your plan.</div>
        {cart?.main && <div className="text-xs text-foreground">Main number (included in your plan): <strong>{cart.main.display}</strong>{cart.main.toll_free ? ' · toll-free' : ''}</div>}
        <div className="text-muted-foreground text-xs">Seat numbers: {(cart?.items || []).filter(i => i.role !== 'main').map(i => i.display).join(', ')}</div>
      </div>
      <p className="text-xs text-muted-foreground">Numbers are bought only after your payment is confirmed. If a number was taken in the meantime, nothing is bought for that seat and you pick another one.</p>
      {note && <p className="text-sm text-amber-700 dark:text-amber-400">{note}</p>}
      <div className="flex flex-wrap justify-between gap-2">
        {company.status === 'pending_payment'
          ? <span />
          : <Button variant="ghost" onClick={onBack}>Back</Button>}
        <div className="flex gap-2">
          {company.status === 'pending_payment' && <Button variant="outline" onClick={confirm} disabled={busy}>I've paid — check again</Button>}
          <Button onClick={pay} disabled={busy || !cart?.complete} data-testid="ts-pay">
            {company.status === 'pending_payment' ? 'Open payment page again' : 'Continue to payment'}
          </Button>
        </div>
      </div>
    </Section>
  );
}

export function ActivationStep({ onFinish, company }) {
  const [data, setData] = useState(null);
  const [repicking, setRepicking] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => ts.provisioning().then(setData).catch(err => toast.error(err.message));
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const retry = async () => {
    setBusy(true);
    try { await ts.retryProvisioning(); await load(); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const repick = async (e164) => {
    setBusy(true);
    try { await ts.repick(repicking, e164); setRepicking(null); await load(); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (!data) return null;
  const s = data.summary;
  const allReady = s.total > 0 && s.voice_ready === s.total;
  return (
    <div className="space-y-4">
      <MainLineCard company={company} numbers={data.numbers} />
      <Section title="Activating your numbers" actions={<Button size="sm" variant="outline" onClick={retry} disabled={busy}><RefreshCw className="w-4 h-4 mr-1" /> Retry unfinished</Button>}>
        <p className="text-sm text-foreground">{s.voice_ready} of {s.total} numbers can make and take calls.</p>
        {!data.live_purchasing && (
          <p className="text-xs text-amber-700 dark:text-amber-400">Number purchasing is not switched on for this environment yet. Your selection is saved and will be bought as soon as it is.</p>
        )}
        <ul className="divide-y divide-border" data-testid="ts-provision-list">
          {data.numbers.map(n => (
            <li key={n.id} className="py-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium w-36">{n.display}</span>
              {n.role === 'main' && <Pill tone="violet">Main · AI</Pill>}
              <VoicePill status={n.status} />
              <SmsPill status={n.sms_status} />
              {n.status === 'unavailable' && <Button size="sm" variant="outline" onClick={() => setRepicking(n.id)}>Pick another number</Button>}
              {n.last_error && n.status !== 'voice_ready' && <span className="text-xs text-muted-foreground">{n.last_error}</span>}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">Calling works as soon as a number shows Voice ready. Texting starts only after your business is approved for SMS (Settings → Business texting). We show it as active only when the carrier confirms.</p>
      </Section>
      {repicking && (
        <Section title="Choose a replacement number" actions={<Button size="sm" variant="ghost" onClick={() => setRepicking(null)}>Cancel</Button>}>
          <NumberSearch onPick={repick} />
        </Section>
      )}
      <div className="flex justify-end gap-2">
        {allReady && <Pill tone="green"><CheckCircle2 className="w-3 h-3 mr-1" /> All numbers ready</Pill>}
        <Button onClick={onFinish} data-testid="ts-finish-setup">Add your staff</Button>
      </div>
    </div>
  );
}

export function WebsiteLearning({ manage = false }) {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    let t;
    const load = () => ts.website().then(d => {
      if (!alive) return;
      setW(d);
      if (d.status === 'learning' || d.status === 'pending') t = setTimeout(load, 3000);
    }).catch(() => {});
    load();
    return () => { alive = false; clearTimeout(t); };
  }, []);
  const relearn = async () => {
    setBusy(true);
    try { setW(await ts.relearnWebsite()); setTimeout(() => ts.website().then(setW), 3000); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  if (!w || w.status === 'none') return null;
  const p = w.profile || {};
  return (
    <Section title={<span className="inline-flex items-center gap-1"><Sparkles className="w-4 h-4 text-violet-500" /> {manage ? 'What your AI learned from your website' : 'Your AI is learning your business'}</span>}>
      <div className="text-sm text-foreground space-y-1" data-testid="ts-website-learning" data-status={w.status}>
        {(w.status === 'learning' || w.status === 'pending') && <p>Reading {w.website}… you can keep going while it learns.</p>}
        {w.status === 'failed' && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-amber-700 dark:text-amber-400">{w.error}</p>
            <Button size="sm" variant="outline" onClick={relearn} disabled={busy}>Try again</Button>
          </div>
        )}
        {w.status === 'ready' && (
          <>
            <p>Learned from {w.pages_read} page{w.pages_read === 1 ? '' : 's'} of {w.website}.</p>
            {p.summary && <p className="text-muted-foreground">{p.summary}</p>}
            <div className="flex flex-wrap gap-1">
              {(p.services || []).slice(0, 8).map(x => <Pill key={x.name} tone="blue">{x.name}{x.price ? ` · ${x.price}` : ''}</Pill>)}
            </div>
            {p.hours && <p className="text-xs text-muted-foreground">Hours: {p.hours}</p>}
            {manage
              ? <Button size="sm" variant="outline" onClick={relearn} disabled={busy} data-testid="ts-relearn">Re-learn my website</Button>
              : <p className="text-xs text-muted-foreground">You can review and add to this any time in AI Brain.</p>}
          </>
        )}
      </div>
    </Section>
  );
}

function MainNumberStep({ company, cart, onCart, onBack, onDone }) {
  const [kind, setKind] = useState('local');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const main = cart?.main;

  const search = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const digits = query.replace(/\D/g, '');
      const params = kind === 'toll_free'
        ? { kind, ...(digits ? { area_code: digits.slice(0, 3) } : {}) }
        : (digits.length === 3 ? { area_code: digits } : { city: query, state: company.address?.state || '' });
      const data = await ts.searchNumbers({ ...params, limit: 20 });
      setResults(data.numbers);
      setMessage(data.message || '');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const pick = async (n) => {
    setBusy(true);
    try { onCart(await ts.cartMain(n)); toast.success(`${n.display} is your main number`); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <Section title="Choose your business main number">
      <p className="text-sm text-muted-foreground">This is your company's main line and its identity in Agent9. When customers call it, your AI receptionist answers as {company.legal_name || 'your business'}, using what it learned from your website, and passes callers to your team. It is also the caller ID when the AI calls your leads. Included in your plan.</p>
      {main && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="ts-main-picked">
          <Pill tone="green"><CheckCircle2 className="w-3 h-3 mr-1" /> Main number: {main.display}{main.toll_free ? ' (toll-free)' : ''}</Pill>
          <span className="text-xs text-muted-foreground">Pick another below to change it.</span>
        </div>
      )}
      <form onSubmit={search} className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-md border border-border overflow-hidden text-xs">
          {[['local', 'Local'], ['toll_free', 'Toll-free']].map(([k, l]) => (
            <button type="button" key={k} onClick={() => { setKind(k); setResults([]); setQuery(''); }} data-testid={`ts-main-kind-${k}`}
              className={`px-3 h-9 ${kind === k ? 'bg-violet-500/15 text-violet-600 dark:text-violet-400' : 'text-muted-foreground'}`}>{l}</button>
          ))}
        </div>
        <Input value={query} onChange={e => setQuery(e.target.value)} className="w-48 h-9" data-testid="ts-main-query"
          placeholder={kind === 'toll_free' ? '800, 888, 877… (optional)' : 'Area code or city'} />
        <Button type="submit" size="sm" disabled={busy || (kind === 'local' && !query.trim())} data-testid="ts-main-search"><Search className="w-4 h-4 mr-1" /> Search</Button>
      </form>
      {message && <p className="text-xs text-amber-700 dark:text-amber-400">{message}</p>}
      {results.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ts-main-results">
          {results.map(n => (
            <div key={n.e164} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${main?.e164 === n.e164 ? 'border-violet-500' : 'border-border'}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{n.display}</div>
                <div className="text-xs text-muted-foreground truncate">{n.toll_free ? 'Toll-free' : [n.locality, n.region].filter(Boolean).join(', ')}</div>
              </div>
              {main?.e164 === n.e164 ? <Pill tone="green">Main</Pill>
                : <Button size="sm" variant="outline" disabled={busy || n.in_cart} onClick={() => pick(n)} aria-label={`Use ${n.display} as main number`}>Use this</Button>}
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button onClick={onDone} disabled={!main} data-testid="ts-main-continue">Continue to seats</Button>
      </div>
    </Section>
  );
}

function MainLineCard({ company, numbers }) {
  const main = (numbers || []).find(n => n.role === 'main');
  if (!main) return null;
  const ready = main.status === 'voice_ready';
  return (
    <Section title={<span className="inline-flex items-center gap-1"><PhoneCall className="w-4 h-4 text-violet-500" /> Your main number</span>}>
      <div className="text-sm space-y-1" data-testid="ts-main-line">
        <div className="text-lg font-semibold text-foreground">{main.display}</div>
        {ready
          ? <p>It's live. Call it now: your AI receptionist answers as {company?.legal_name || 'your business'}.</p>
          : <p className="text-muted-foreground">Setting it up… it starts answering as soon as it shows Voice ready.</p>}
        {company?.contact_phone && <p className="text-xs text-muted-foreground">Keep your current number: ask your phone carrier to forward {company.contact_phone} to {main.display}, and your existing customers reach the AI too.</p>}
      </div>
    </Section>
  );
}
